import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import ReportDialog from '../components/ReportDialog';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

function ScoreInput({ value, onChange }) {
  return (
    <input type="number" min="1" max="10" value={value || ''} onChange={e => onChange(parseInt(e.target.value) || '')}
      style={{ width: 56, padding: '5px 8px', border: '1.5px solid #ddd', borderRadius: 6, textAlign: 'center', fontSize: 15, fontWeight: 700, color: 'var(--accent)' }} />
  );
}

function ScoreBar({ value, max = 10 }) {
  return (
    <div style={{ height: 6, background: '#f0e8ef', borderRadius: 3, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', background: 'linear-gradient(90deg,#7B2D6B,#E8541A)', width: ((value || 0) / max * 100) + '%', borderRadius: 3 }} />

    </div>
  );
}

export default function TastePanelSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [panelists, setPanelists] = useState([]);
  const [samples, setSamples] = useState([{ sample_name: 'Sample A', trial_ref: '' }]);
  const [selectedSample, setSelectedSample] = useState('Sample A');
  const [editingDesc, setEditingDesc] = useState(null);
  const [params, setParams] = useState([]);
  const [customValues, setCustomValues] = useState([]);
  const [customForm, setCustomForm] = useState({});
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPrefill, setReportPrefill] = useState(null);
  const buildReportPrefill = () => {
    const scoreParamsList = [
      ...(stdParams ? stdParams.map(p => ({ key: p.param_key, label: p.label })) : [['appearance','Appearance'],['aroma','Aroma'],['texture','Texture'],['taste','Taste'],['aftertaste','Aftertaste'],['overall','Overall']].map(([k,l]) => ({ key: k, label: l }))),
      ...customParams.filter(p => p.param_type === 'score').map(p => ({ key: p.param_key, label: p.label }))
    ];
    const avgFor = (attr, sn) => {
      if (customParams.some(p => p.param_key === attr)) { const v = customAvg(attr, sn); return v ? Math.round(v * 10) / 10 : null; }
      const list = (session.scores || []).filter(sc => (sc.sample_name || 'Sample A') === sn && sc[attr] != null);
      return list.length ? Math.round(list.reduce((s, sc) => s + (parseFloat(sc[attr]) || 0), 0) / list.length * 10) / 10 : null;
    };
    const sampleObjs = samples.map(sm => ({ name: sm.sample_name, averages: Object.fromEntries(scoreParamsList.map(p => [p.key, avgFor(p.key, sm.sample_name)])) }));
    const ranked = [...sampleObjs].sort((a, b) => (b.averages.overall || 0) - (a.averages.overall || 0));
    const participants = new Set((session.scores || []).map(sc => sc.panelist_id)).size;
    const top = ranked[0];
    const recs = (session.scores || []).reduce((o, sc) => { if (sc.recommendation) o[sc.recommendation] = (o[sc.recommendation] || 0) + 1; return o; }, {});
    const interp = sampleObjs.map(so => {
      const entries = scoreParamsList.map(p => [p.label, so.averages[p.key]]).filter(e => e[1] != null && e[0] !== 'Overall');
      if (!entries.length) return so.name + ': no scores recorded.';
      const hi = entries.reduce((a, b) => b[1] > a[1] ? b : a); const lo = entries.reduce((a, b) => b[1] < a[1] ? b : a);
      return so.name + ' scored strongest on ' + hi[0] + ' (' + hi[1] + ') and weakest on ' + lo[0] + ' (' + lo[1] + ').';
    }).join('\n') + (ranked.length > 1 && top.averages.overall != null && ranked[1].averages.overall != null ? '\n' + top.name + ' outperformed ' + ranked[1].name + ' on overall likability by ' + (Math.round((top.averages.overall - ranked[1].averages.overall) * 10) / 10) + ' points.' : '');
    return {
      sections: {
        aim: 'To evaluate the sensory characteristics of ' + (session.product_name || 'the product') + ' across ' + samples.length + ' sample(s) and identify the preferred formulation.',
        reason: 'Sensory evaluation conducted by the Tasting Folks panel as part of the NPD process' + (session.batch_ref ? ' (ref: ' + session.batch_ref + ')' : '') + '.',
        product_details: 'Product: ' + (session.product_name || '') + '\nProduct Code: ' + (session.product_code || '') + '\nSession: ' + (session.session_code || '') + ' · ' + (session.session_date || ''),
        sample_coding: samples.map(sm => ({ code: sm.sample_name, description: sm.internal_description || '', status: sm.trial_ref ? 'Trial ' + sm.trial_ref : 'Evaluated' })),
        procedure: 'All samples were evaluated under identical tasting conditions. Panelists scored each parameter on a 0-10 scale through the Tasting Folks portal. Comments were mandatory for each sample.',
        sensory_eval: 'Panel of ' + participants + ' member(s) assessed: ' + scoreParamsList.map(p => p.label).join(', ') + '.',
        interpretation: interp,
        conclusion_ranking: ranked.filter(r => r.averages.overall != null).map((r, i) => (i + 1) + '. ' + r.name + ' — overall ' + r.averages.overall + '/10').join('\n'),
        final_conclusion: top && top.averages.overall != null ? (top.name + ' ranked highest (' + top.averages.overall + '/10)' + (Object.keys(recs).length ? '. Panel recommendations: ' + Object.entries(recs).map(([k, v]) => v + ' ' + k).join(', ') + '.' : '.')) : 'No overall scores recorded yet.',
        approved_by: 'R&D Manager'
      },
      meta: { product_name: session.product_name, product_code: session.product_code, session_code: session.session_code, session_date: session.session_date, session_type: session.session_type, participants },
      results: { params: scoreParamsList, samples: sampleObjs, ranking: ranked.map(r => r.name) }
    };
  };
  const STD_KEYS = ['appearance','aroma','texture','taste','aftertaste','overall','value'];
  const ICONS = { appearance:'🎨', aroma:'👃', texture:'👄', taste:'🌶', aftertaste:'💫', overall:'⭐', value:'💰' };
  const activeParams = params.length ? params : null;
  const stdParams = activeParams ? activeParams.filter(p => STD_KEYS.includes(p.param_key)) : null;
  const customParams = activeParams ? activeParams.filter(p => !STD_KEYS.includes(p.param_key)) : [];
  const customAvg = (key, sample) => {
    const vals = customValues.filter(v => v.param_key === key && v.sample_name === sample && v.value_num !== null).map(v => v.value_num);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  };
  const customTexts = (key, sample) => customValues.filter(v => v.param_key === key && v.sample_name === sample && v.value_text).map(v => ({ pid: v.panelist_id, text: v.value_text }));
  const cvFor = (pid, sample, key) => {
    const row = customValues.find(v => v.panelist_id === pid && v.sample_name === sample && v.param_key === key);
    return row ? (row.value_num !== null ? row.value_num : row.value_text) : null;
  };
  const [descDraft, setDescDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scores');
  const [tierFilter, setTierFilter] = useState('all');
  const [selectedPanelist, setSelectedPanelist] = useState(null);
  const [scoreForm, setScoreForm] = useState({ appearance: '', aroma: '', texture: '', taste: '', aftertaste: '', overall: '', comments: '', recommendation: '' });
  const [showAddPanelist, setShowAddPanelist] = useState(false);
  const [showAddSample, setShowAddSample] = useState(false);
  const [newPanelist, setNewPanelist] = useState({ name: '', role: '', expertise: '' });
  const [newSample, setNewSample] = useState({ sample_name: '', trial_ref: '', internal_description: '' });

  const load = useCallback(() => {
    Promise.all([
      API.get('/taste/sessions/' + id),
      API.get('/taste/panelists'),
      API.get('/taste/sessions/' + id + '/samples'),
      API.get('/taste/sessions/' + id + '/params').then(r => setParams(r.data)).catch(()=>{}),
      API.get('/taste/sessions/' + id + '/score-values').then(r => setCustomValues(r.data)).catch(()=>{}),
    ]).then(([s, p, sm]) => {
      setSession(s.data);
      setPanelists(p.data);
      const sampleList = sm.data.length > 0 ? sm.data : [{ id: 0, sample_name: 'Sample A', trial_ref: '' }];
      setSamples(sampleList);
      setSelectedSample(sampleList[0].sample_name);
      if (s.data.session_type === 'consumer') setActiveTab('responses');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const selectPanelist = (p) => {
    setSelectedPanelist(p);
    const existing = session?.scores?.find(s => s.panelist_id === p.id && (s.sample_name === selectedSample || (!s.sample_name && selectedSample === 'Sample A')));
    if (existing) {
      setScoreForm({ appearance: existing.appearance, aroma: existing.aroma, texture: existing.texture, taste: existing.taste, aftertaste: existing.aftertaste, overall: existing.overall, comments: existing.comments || '', recommendation: existing.recommendation || '' });
      setCustomForm(customParams.reduce((o, p) => ({ ...o, [p.param_key]: cvFor(p.id, selectedSample, p.param_key) ?? '' }), {}));
    } else {
      setScoreForm({ appearance: '', aroma: '', texture: '', taste: '', aftertaste: '', overall: '', comments: '', recommendation: '' });
    }
  };

  const saveScore = async () => {
    if (!selectedPanelist) return toast.error('Select a panelist');
    const attrs = stdParams ? stdParams.map(p => p.param_key === 'value' ? 'aftertaste' : p.param_key) : ['appearance','aroma','texture','taste','aftertaste','overall'];
    for (const a of attrs) { if (!scoreForm[a] || scoreForm[a] < 1 || scoreForm[a] > 10) return toast.error('All scores must be between 1-10'); }
    await API.post('/taste/scores', { session_id: id, panelist_id: selectedPanelist.id, panelist_name: selectedPanelist.name, sample_name: selectedSample, ...scoreForm, custom_values: customForm });
    toast.success('Scores saved!');
    load();
  };

  const addSample = async () => {
    if (!newSample.sample_name) return toast.error('Sample name required');
    const r = await API.post('/taste/sessions/' + id + '/samples', newSample);
    const newS = { id: r.data.id, sample_name: newSample.sample_name, trial_ref: newSample.trial_ref, internal_description: newSample.internal_description };
    setSamples(prev => [...prev, newS]);
    setSelectedSample(newSample.sample_name);
    setShowAddSample(false);
    setNewSample({ sample_name: '', trial_ref: '' });
    toast.success('Sample added!');
  };

  const addPanelist = async () => {
    if (!newPanelist.name) return toast.error('Name required');
    await API.post('/taste/panelists', newPanelist);
    toast.success('Panelist added!');
    setShowAddPanelist(false);
    setNewPanelist({ name: '', role: '', expertise: '' });
    load();
  };

  const closeSession = async () => {
    if (!window.confirm('Close this session? No more entries will be accepted.')) return;
    await API.put('/taste/sessions/' + id + '/status', { status: 'closed' });
    toast.success('Session closed');
    load();
  };

  const qrUrl = window.location.origin + '/review/' + session?.session_code;

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;
  if (!session) return <div className="empty-state"><p>Session not found</p></div>;

  const TIERS = [
    { key: 'all', label: 'All', icon: '👥' },
    { key: 'emerging', label: 'Emerging', icon: '🌱' },
    { key: 'silver', label: 'Silver', icon: '⭐' },
    { key: 'gold', label: 'Gold', icon: '👑' },
    { key: 'golden', label: 'Golden', icon: '🏆' },
  ];
  const sampleScores = (sampleName) => session.scores?.filter(s => s.sample_name === sampleName || (!s.sample_name && sampleName === 'Sample A')) || [];
  const allCurrentScores = sampleScores(selectedSample);
  const currentScores = tierFilter === 'all' ? allCurrentScores : allCurrentScores.filter(s => (s.tier || 'emerging') === tierFilter);

  const avgScore = session.session_type === 'consumer'
    ? (session.responses?.length > 0 ? session.responses.reduce((s, r) => s + (r.appearance_score + r.aroma_score + r.texture_score + r.taste_score + r.value_score) / 5, 0) / session.responses.length : null)
    : (currentScores.length > 0 ? currentScores.reduce((s, sc) => s + (sc.appearance + sc.aroma + sc.texture + sc.taste + sc.aftertaste + sc.overall) / 6, 0) / currentScores.length : null);

  const CONSUMER_ATTR_MAP = { appearance: 'appearance_score', aroma: 'aroma_score', texture: 'texture_score', taste: 'taste_score', aftertaste: 'value_score', overall: null };

  const attrAvg = (attr) => {
    if (session.session_type === 'consumer') {
      const mappedKey = CONSUMER_ATTR_MAP[attr];
      if (!mappedKey || !session.responses?.length) return 0;
      return session.responses.reduce((s, r) => s + (r[mappedKey] || 0), 0) / session.responses.length;
    }
    if (!currentScores.length) return 0;
    return currentScores.reduce((s, sc) => s + (sc[attr] || 0), 0) / currentScores.length;
  };

  const ATTRS = session.session_type === 'consumer'
    ? (stdParams ? stdParams.map(p => [p.param_key === 'value' ? 'aftertaste' : p.param_key, (ICONS[p.param_key]||'') + ' ' + p.label]) : [['appearance', '🎨 Appearance'], ['aroma', '👃 Aroma'], ['texture', '👄 Texture'], ['taste', '🌶 Taste'], ['aftertaste', '💰 Value for Money']])
    : (stdParams ? stdParams.map(p => [p.param_key, (ICONS[p.param_key]||'') + ' ' + p.label]) : [['appearance', '🎨 Appearance'], ['aroma', '👃 Aroma'], ['texture', '👄 Texture'], ['taste', '🌶 Taste'], ['aftertaste', '💫 Aftertaste'], ['overall', '⭐ Overall']]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => navigate('/taste-panel')}>Tasting Folks</span> › {session.session_code}
          </div>
          <div className="page-title">{session.session_code} — {session.product_name}</div>
          <div className="page-subtitle">{session.session_type === 'elite' ? 'Tasting Folks' : 'Consumer'} · {session.session_date || '—'} {session.batch_ref ? '· ' + session.batch_ref : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ background: session.status === 'open' ? '#fff3e0' : '#e8f5e9', color: session.status === 'open' ? '#e67e00' : '#2e7d32', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{session.status}</span>
          {session.session_type === 'elite' && session.scores?.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setReportPrefill(buildReportPrefill()); setReportOpen(true); }}>⬇ Sensory Report</button>
          )}
          {session.status === 'open' && <button className="btn btn-secondary btn-sm" onClick={closeSession}>🔒 Close Session</button>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-value">{session.session_type === 'elite' ? (session.scores?.length || 0) : (session.responses?.length || 0)}</div><div className="stat-label">Responses</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--accent)' }}>{avgScore ? avgScore.toFixed(1) : '—'}</div><div className="stat-label">Avg Score</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: '#2e7d32' }}>{session.scores?.filter(s => s.recommendation === 'accept').length || 0}</div><div className="stat-label">Accept</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: '#e67e00' }}>{samples.length}</div><div className="stat-label">Samples</div></div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {session.session_type === 'elite' && <button className={`tab ${activeTab === 'scores' ? 'active' : ''}`} onClick={() => setActiveTab('scores')}>Score Entry</button>}
        <button className={`tab ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')}>Results</button>
        {session.session_type === 'elite' && samples.length > 1 && <button className={`tab ${activeTab === 'compare' ? 'active' : ''}`} onClick={() => setActiveTab('compare')}>Compare Samples</button>}
        {session.session_type === 'consumer' && <button className={`tab ${activeTab === 'qr' ? 'active' : ''}`} onClick={() => setActiveTab('qr')}>QR Code</button>}
        {session.session_type === 'consumer' && <button className={`tab ${activeTab === 'responses' ? 'active' : ''}`} onClick={() => setActiveTab('responses')}>Responses ({session.responses?.length || 0})</button>}
      </div>

      {activeTab === 'scores' && session.session_type === 'elite' && (
        <div>
          {/* Sample selector */}
          <div className="card" style={{ marginBottom: 12, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sample</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {samples.map(s => (
                <button key={s.sample_name} onClick={() => { setSelectedSample(s.sample_name); setSelectedPanelist(null); setScoreForm({ appearance: '', aroma: '', texture: '', taste: '', aftertaste: '', overall: '', comments: '', recommendation: '' }); }}
                  className={'btn btn-sm ' + (selectedSample === s.sample_name ? 'btn-primary' : 'btn-secondary')}
                  style={{ fontSize: 12 }}>
                  {s.sample_name}{s.trial_ref ? ' · ' + s.trial_ref : ''}
                </button>
              ))}
              {(() => { const cs = samples.find(x => x.sample_name === selectedSample); if (!cs || !cs.id) return null; return (
                <div style={{ width: '100%', fontSize: 12, color: 'var(--text-muted, #777)', background: '#faf5fa', border: '1px dashed var(--accent, #7B2D6B)', borderRadius: 8, padding: '6px 10px', marginTop: 6 }}>
                  {editingDesc === cs.id ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <textarea autoFocus value={descDraft} onChange={e => setDescDraft(e.target.value)} rows={2}
                        style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--accent)', borderRadius: 6, fontFamily: 'inherit', fontSize: 12 }} />
                      <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={async () => {
                        try {
                          await API.put('/taste/samples/' + cs.id, { internal_description: descDraft });
                          setSamples(list => list.map(x => x.id === cs.id ? { ...x, internal_description: descDraft } : x));
                          setEditingDesc(null); toast.success('Description saved');
                        } catch { toast.error('Save failed'); }
                      }}>💾</button>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setEditingDesc(null)}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        📝 <b>{selectedSample}:</b> {cs.internal_description || <i>no internal description</i>} <span style={{ fontSize: 10, color: '#e65100' }}>(internal — not visible to panelists)</span>
                      </div>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => { setDescDraft(cs.internal_description || ''); setEditingDesc(cs.id); }}>✎ Edit</button>
                    </div>
                  )}
                </div>
              ); })()}
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddSample(true)} style={{ fontSize: 12 }}>+ Add Sample</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>
            <div>
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 14, color: 'var(--accent)' }}>Panelists</div>
                {panelists.map(p => {
                  const scored = sampleScores(selectedSample).find(s => s.panelist_id === p.id);
                  return (
                    <div key={p.id} onClick={() => selectPanelist(p)} style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 6, border: `1.5px solid ${selectedPanelist?.id === p.id ? 'var(--accent)' : 'var(--border)'}`, background: selectedPanelist?.id === p.id ? 'var(--accent-dim)' : 'white', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#7B2D6B,#E8541A)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{p.name.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.role}</div>
                      </div>
                      {scored && <span style={{ fontSize: 10, background: '#e8f5e9', color: '#2e7d32', padding: '2px 6px', borderRadius: 10, fontWeight: 700 }}>✓</span>}
                    </div>
                  );
                })}
                <button className="btn btn-outline btn-sm" style={{ width: '100%', marginTop: 8 }} onClick={() => setShowAddPanelist(true)}>+ Add Panelist</button>
              </div>
            </div>

            <div className="card">
              {!selectedPanelist ? (
                <div className="empty-state"><div className="empty-state-icon">👆</div><p>Select a panelist to enter scores for <b>{selectedSample}</b></p></div>
              ) : (
                <>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--accent)' }}>Scoring — {selectedPanelist.name} · {selectedSample}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>{selectedPanelist.role}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                    <thead><tr><th style={{ padding: '6px 0', fontSize: 11, textAlign: 'left', color: '#aaa', textTransform: 'uppercase' }}>Attribute</th><th style={{ padding: '6px 8px', fontSize: 11, textAlign: 'center', color: '#aaa', textTransform: 'uppercase', width: 80 }}>Score (1-10)</th><th></th></tr></thead>
                    <tbody>
                      {(stdParams ? stdParams.map(p => [p.param_key, (ICONS[p.param_key]||'') + ' ' + p.label]) : [['appearance','🎨 Appearance'],['aroma','👃 Aroma'],['texture','👄 Texture'],['taste','🌶 Taste'],['aftertaste','💫 Aftertaste'],['overall','⭐ Overall']]).map(([key, lbl]) => (
                        <tr key={key} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '10px 0', fontSize: 14 }}>{lbl}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}><ScoreInput value={scoreForm[key]} onChange={v => setScoreForm({...scoreForm, [key]: v})} /></td>
                          <td style={{ padding: '6px 8px' }}><ScoreBar value={scoreForm[key]} /></td>
                        </tr>
                      ))}
                      {customParams.map(p => (
                        <tr key={p.param_key} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '10px 0', fontSize: 14 }}>✳ {p.label}</td>
                          {p.param_type === 'score' ? (
                            <>
                              <td style={{ padding: '6px 8px', textAlign: 'center' }}><ScoreInput value={customForm[p.param_key] ?? ''} onChange={v => setCustomForm(f => ({ ...f, [p.param_key]: v }))} /></td>
                              <td style={{ padding: '6px 8px' }}><ScoreBar value={customForm[p.param_key]} /></td>
                            </>
                          ) : (
                            <td colSpan={2} style={{ padding: '6px 8px' }}>
                              <input value={customForm[p.param_key] ?? ''} onChange={e => setCustomForm(f => ({ ...f, [p.param_key]: e.target.value }))}
                                placeholder={p.label} style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, color: '#111', background: '#fff' }} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 5 }}>Comments</label>
                    <textarea value={scoreForm.comments} onChange={e => setScoreForm({...scoreForm, comments: e.target.value})} rows={3} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8, resize: 'vertical', fontSize: 13 }} placeholder="Detailed observations..." />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 8 }}>Recommendation</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[['accept', '✓ Accept', '#e8f5e9', '#2e7d32'], ['modify', '⚙ Modify', '#fff3e0', '#e67e00'], ['reject', '✗ Reject', '#fce8e6', '#c62828']].map(([val, lbl, bg, col]) => (
                        <button key={val} onClick={() => setScoreForm({...scoreForm, recommendation: val})}
                          style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${scoreForm.recommendation === val ? col : '#eee'}`, background: scoreForm.recommendation === val ? bg : 'white', color: col, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={saveScore}>💾 Save Scores</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'compare' && session.session_type === 'elite' && (() => {
        const SAMPLE_COLORS = ['#7B2D6B', '#E8541A', '#2e7d32', '#1565c0', '#f9a825', '#6d4c41'];
        const attrAvgFor = (attr, sampleName) => {
          const list = (session.scores || []).filter(sc => (sc.sample_name || 'Sample A') === sampleName && sc[attr] != null);
          return list.length ? list.reduce((s, sc) => s + (parseFloat(sc[attr]) || 0), 0) / list.length : 0;
        };
        const customAvgFor = (key, sampleName) => customAvg(key, sampleName);
        const axes = [
          ...(stdParams ? stdParams.map(p => [p.param_key, p.label]) : [['appearance','Appearance'],['aroma','Aroma'],['texture','Texture'],['taste','Taste'],['aftertaste','Aftertaste'],['overall','Overall']]),
          ...customParams.filter(p => p.param_type === 'score').map(p => [p.param_key, '✳ ' + p.label])
        ];
        const isCustom = (k) => customParams.some(p => p.param_key === k);
        const overallData = samples.map(s => ({ name: s.sample_name, score: parseFloat(attrAvgFor('overall', s.sample_name).toFixed(1)) }));
        const radarData = axes.map(([key, lbl]) => {
          const row = { subject: lbl };
          samples.forEach(s => { row[s.sample_name] = parseFloat((isCustom(key) ? customAvgFor(key, s.sample_name) : attrAvgFor(key, s.sample_name)).toFixed(1)); });
          return row;
        });
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Overall Likability by Sample</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={overallData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ef" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                    {overallData.map((_, i) => <Cell key={i} fill={SAMPLE_COLORS[i % SAMPLE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>All Parameters — Sample Comparison</div>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData} outerRadius="70%">
                  <PolarGrid stroke="#e8dce6" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                  {samples.map((s, i) => (
                    <Radar key={s.sample_name} name={s.sample_name} dataKey={s.sample_name}
                      stroke={SAMPLE_COLORS[i % SAMPLE_COLORS.length]} fill={SAMPLE_COLORS[i % SAMPLE_COLORS.length]} fillOpacity={0.18} strokeWidth={2} />
                  ))}
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })()}

      {activeTab === 'results' && (
        <div>
          {/* Sample tabs for results */}
          {session.session_type === 'elite' && samples.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {samples.map(s => (
                <button key={s.sample_name} onClick={() => setSelectedSample(s.sample_name)}
                  className={'btn btn-sm ' + (selectedSample === s.sample_name ? 'btn-primary' : 'btn-secondary')}>
                  {s.sample_name}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 16, color: 'var(--accent)' }}>
                Attribute Averages {session.session_type === 'elite' && samples.length > 1 ? '— ' + selectedSample : ''}
              </div>
              {ATTRS.map(([key, lbl]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 140, fontSize: 13 }}>{lbl}</div>
                  <ScoreBar value={attrAvg(key)} />
                  <b style={{ width: 30, textAlign: 'right', fontSize: 13 }}>{attrAvg(key).toFixed(1)}</b>
                </div>
              ))}
              {customParams.filter(p => p.param_type === 'score').map(p => (
                <div key={p.param_key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 140, fontSize: 13 }}>✳ {p.label}</div>
                  <ScoreBar value={customAvg(p.param_key, selectedSample)} />
                  <b style={{ width: 30, textAlign: 'right', fontSize: 13 }}>{customAvg(p.param_key, selectedSample).toFixed(1)}</b>
                </div>
              ))}
              {customParams.filter(p => p.param_type === 'text').map(p => {
                const texts = customTexts(p.param_key, selectedSample);
                return texts.length ? (
                  <div key={p.param_key} style={{ marginTop: 10, padding: 10, background: '#f9f3f8', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 6 }}>✳ {p.label.toUpperCase()}</div>
                    {texts.map((t, i) => <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>• {t.text}</div>)}
                  </div>
                ) : null;
              })}
              {session.session_type === 'consumer' && session.responses?.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: '#f9f3f8', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>WOULD BUY?</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['yes','✓ Yes','#e8f5e9','#2e7d32'],['maybe','? Maybe','#fff3e0','#e67e00'],['no','✗ No','#fce8e6','#c62828']].map(([val,lbl,bg,col]) => {
                      const count = session.responses.filter(r => r.would_buy === val).length;
                      const pct = Math.round(count / session.responses.length * 100);
                      return <div key={val} style={{ flex:1, background:bg, borderRadius:8, padding:'8px', textAlign:'center' }}><b style={{ color:col, fontSize:14 }}>{pct}%</b><div style={{ fontSize:10, color:col }}>{lbl} ({count})</div></div>;
                    })}
                  </div>
                </div>
              )}
              {session.session_type === 'elite' && currentScores.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: '#f9f3f8', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>RECOMMENDATIONS</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[['accept','✓ Accept','#e8f5e9','#2e7d32'],['modify','⚙ Modify','#fff3e0','#e67e00'],['reject','✗ Reject','#fce8e6','#c62828']].map(([val,lbl,bg,col]) => {
                      const count = currentScores.filter(s => s.recommendation === val).length;
                      return count > 0 && <span key={val} style={{ background:bg, color:col, padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>{lbl} ×{count}</span>;
                    })}
                  </div>
                </div>
              )}
            </div>
            {/* Tier Filter */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              {TIERS.map(t => {
                const count = t.key === 'all' ? allCurrentScores.length : allCurrentScores.filter(s => (s.tier || 'emerging') === t.key).length;
                if (count === 0 && t.key !== 'all') return null;
                return (
                  <button key={t.key} onClick={() => setTierFilter(t.key)}
                    style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1.5px solid ' + (tierFilter === t.key ? 'var(--accent)' : 'var(--border)'), background: tierFilter === t.key ? 'var(--accent-dim)' : 'white', color: tierFilter === t.key ? 'var(--accent)' : 'var(--text-muted)' }}>
                    {t.icon} {t.label} ({count})
                  </button>
                );
              })}
            </div>
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 16, color: 'var(--accent)' }}>Individual Scores {tierFilter !== 'all' ? '— ' + TIERS.find(t=>t.key===tierFilter)?.label + ' Tier' : ''}</div>
              {currentScores.length === 0 ? <div className="empty-state"><p>No scores yet</p></div> : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead><tr style={{ background: 'var(--bg-elevated)' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Panelist</th>
                        {[['appearance','🎨'],['aroma','👃'],['texture','👄'],['taste','🌶'],['aftertaste','💫'],['overall','⭐']].map(([k,l]) => <th key={k} style={{ padding: '6px 4px', textAlign: 'center', fontSize: 10 }}>{l}</th>)}
                        <th style={{ padding: '6px 4px', textAlign: 'center', fontSize: 10 }}>Rec</th>
                      </tr></thead>
                      <tbody>
                        {currentScores.map(sc => (
                          <tr key={sc.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '8px', fontWeight: 600 }}>
                              {sc.panelist_name}
                              {sc.tier && <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.7 }}>{sc.tier === 'emerging' ? '🌱' : sc.tier === 'silver' ? '⭐' : sc.tier === 'gold' ? '👑' : '🏆'}</span>}
                            </td>
                            {['appearance','aroma','texture','taste','aftertaste','overall'].map(k => <td key={k} style={{ padding: '8px 4px', textAlign: 'center' }}>{sc[k]}</td>)}
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <span style={{ color: sc.recommendation === 'accept' ? '#2e7d32' : sc.recommendation === 'reject' ? '#c62828' : '#e67e00', fontWeight: 700 }}>
                                {sc.recommendation === 'accept' ? '✓' : sc.recommendation === 'reject' ? '✗' : '⚙'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>COMMENTS</div>
                    {currentScores.filter(s => s.comments).map(sc => (
                      <div key={sc.id} style={{ fontSize: 12, color: '#555', padding: '8px 10px', background: '#f9f9f9', borderRadius: 6, marginBottom: 6 }}>
                        <b>{sc.panelist_name}:</b> {sc.comments}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Charts */}
          {session.session_type === 'consumer' && session.responses?.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
              {/* Would Buy Chart */}
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Would Buy?</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={[
                    { name: 'Yes', yes: session.responses.filter(r => r.would_buy === 'yes').length },
                    { name: 'Maybe', maybe: session.responses.filter(r => r.would_buy === 'maybe').length },
                    { name: 'No', no: session.responses.filter(r => r.would_buy === 'no').length },
                  ]} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ef" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="yes" fill="#2e7d32" radius={[4,4,0,0]} />
                    <Bar dataKey="maybe" fill="#e67e00" radius={[4,4,0,0]} />
                    <Bar dataKey="no" fill="#c62828" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8 }}>
                  {[['yes','#e8f5e9','#2e7d32'],['maybe','#fff3e0','#e67e00'],['no','#fce8e6','#c62828']].map(([val,bg,col]) => {
                    const count = session.responses.filter(r=>r.would_buy===val).length;
                    const pct = Math.round(count/session.responses.length*100);
                    return <div key={val} style={{ background:bg, borderRadius:8, padding:'4px 10px', textAlign:'center' }}><b style={{ color:col, fontSize:13 }}>{pct}%</b><div style={{ fontSize:10, color:col }}>{val}</div></div>;
                  })}
                </div>
              </div>
              {/* Attribute Scores */}
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Attribute Scores</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={[['appearance','👁 Appearance'],['aroma','👃 Aroma'],['texture','👄 Texture'],['taste','🌶 Taste'],['value','💰 Value']].map(([key, lbl]) => ({
                    name: lbl.split(' ').slice(1).join(' '),
                    score: session.responses.length > 0 ? parseFloat((session.responses.reduce((s,r) => s+(r[key+'_score']||0),0)/session.responses.length).toFixed(1)) : 0
                  }))} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ef" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => [v + '/5', 'Score']} />
                    <Bar dataKey="score" fill="#E8541A" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Radar */}
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Sensory Profile</div>
                <ResponsiveContainer width="100%" height={180}>
                  <RadarChart data={[['appearance','Appearance'],['aroma','Aroma'],['texture','Texture'],['taste','Taste'],['value','Value']].map(([key, lbl]) => ({
                    subject: lbl,
                    A: session.responses.length > 0 ? parseFloat((session.responses.reduce((s,r) => s+(r[key+'_score']||0),0)/session.responses.length*2).toFixed(1)) : 0
                  }))}>
                    <PolarGrid stroke="#f0e8ef" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#E8541A', fontWeight: 600 }} />
                    <PolarRadiusAxis angle={90} domain={[0,10]} tick={{ fontSize: 8 }} />
                    <Radar name="Consumer" dataKey="A" stroke="#E8541A" fill="#E8541A" fillOpacity={0.3} />
                    <Tooltip formatter={(v) => [v.toFixed(1), 'Score']} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {(session.session_type === 'elite' ? currentScores.length > 0 : session.responses?.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 16, color: 'var(--accent)' }}>
                  Attribute Scores — Bar Chart {session.session_type === 'elite' && samples.length > 1 ? '· ' + selectedSample : ''}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={[...ATTRS.map(([key, lbl]) => ({ name: lbl.split(' ').slice(1).join(' '), score: parseFloat(attrAvg(key).toFixed(1)) })), ...customParams.filter(p => p.param_type === 'score').map(p => ({ name: p.label, score: parseFloat(customAvg(p.param_key, selectedSample).toFixed(1)) }))]} margin={{ top: 5, right: 10, left: -20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ef" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                    <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => [v.toFixed(1), 'Score']} />
                    <Bar dataKey="score" fill="#7B2D6B" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 16, color: 'var(--accent)' }}>
                  Sensory Profile — Radar Chart {session.session_type === 'elite' && samples.length > 1 ? '· ' + selectedSample : ''}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  {(() => {
                    const radarData = [...ATTRS.map(([key, lbl]) => ({ subject: lbl.split(' ').slice(1).join(' '), A: parseFloat(attrAvg(key).toFixed(1)) })), ...customParams.filter(p => p.param_type === 'score').map(p => ({ subject: p.label, A: parseFloat(customAvg(p.param_key, selectedSample).toFixed(1)) }))];
                    return (
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#f0e8ef" />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#7B2D6B', fontWeight: 600 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fontSize: 8 }} />
                        <Radar name={session.session_type === 'elite' ? (selectedSample || 'Sample A') : 'Consumer'} dataKey="A" stroke="#7B2D6B" fill="#7B2D6B" fillOpacity={0.3} />
                        <Tooltip formatter={(v) => [v.toFixed(1), 'Score']} />
                        <Legend />
                      </RadarChart>
                    );
                  })()}
                </ResponsiveContainer>
              </div>
            </div>
          )}

        </div>
      )}

      {activeTab === 'qr' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card" style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ marginBottom: 16 }}>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`} alt="QR Code" style={{ borderRadius: 8, border: '4px solid #f0e8ef' }} />
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Scan to submit review</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', wordBreak: 'break-all', marginBottom: 20 }}>{qrUrl}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrUrl)}`} download="qr-code.png" className="btn btn-primary btn-sm">⬇ Download PNG</a>
              <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(qrUrl); toast.success('Link copied!'); }}>📋 Copy Link</button>
            </div>
          </div>
          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 16, color: 'var(--accent)' }}>Live Stats</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ background: '#f9f3f8', borderRadius: 8, padding: 16, textAlign: 'center' }}><div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)' }}>{session.responses?.length || 0}</div><div style={{ fontSize: 11, color: '#888' }}>Total Responses</div></div>
              <div style={{ background: '#f9f3f8', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#E8541A' }}>
                  {session.responses?.length > 0 ? (session.responses.reduce((s, r) => s + (r.appearance_score + r.aroma_score + r.texture_score + r.taste_score + r.value_score) / 5, 0) / session.responses.length).toFixed(1) + '★' : '—'}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>Avg Score</div>
              </div>
            </div>
            {session.responses?.length > 0 && (
              <>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 8, fontWeight: 700 }}>WOULD BUY?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['yes','#e8f5e9','#2e7d32'],['maybe','#fff3e0','#e67e00'],['no','#fce8e6','#c62828']].map(([val, bg, col]) => {
                    const count = session.responses.filter(r => r.would_buy === val).length;
                    const pct = Math.round(count / session.responses.length * 100);
                    return <div key={val} style={{ flex:1, background:bg, borderRadius:8, padding:'8px', textAlign:'center' }}><b style={{ color:col, fontSize:14 }}>{pct}%</b><div style={{ fontSize:10, color:col }}>{val} ({count})</div></div>;
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'responses' && (
        <div className="card">
          {session.responses?.length === 0 ? <div className="empty-state"><p>No responses yet</p></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Nationality</th><th>Age</th><th>App</th><th>Aroma</th><th>Tex</th><th>Taste</th><th>Value</th><th>Would Buy</th><th>Date</th></tr></thead>
                <tbody>
                  {session.responses.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{r.respondent_name}</td>
                      <td style={{ fontSize: 12 }}>{r.nationality}</td>
                      <td style={{ fontSize: 12 }}>{r.age_group}</td>
                      {['appearance_score','aroma_score','texture_score','taste_score','value_score'].map(k => (
                        <td key={k} style={{ textAlign: 'center' }}>
                          <span style={{ background: '#f9f3f8', color: 'var(--accent)', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{r[k] || '—'}</span>
                        </td>
                      ))}
                      <td><span style={{ background: r.would_buy === 'yes' ? '#e8f5e9' : r.would_buy === 'no' ? '#fce8e6' : '#fff3e0', color: r.would_buy === 'yes' ? '#2e7d32' : r.would_buy === 'no' ? '#c62828' : '#e67e00', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{r.would_buy}</span></td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-AE') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showAddSample && (
        <div className="modal-overlay" onClick={() => setShowAddSample(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><div className="modal-title">Add Sample</div><button className="modal-close" onClick={() => setShowAddSample(false)}>✕</button></div>
            <div style={{ padding: '8px 0' }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 4 }}>Sample Name *</label>
                <input value={newSample.sample_name} onChange={e => setNewSample({...newSample, sample_name: e.target.value})} placeholder="e.g. Sample B" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 4 }}>Trial Ref (optional)</label>
                <input value={newSample.trial_ref} onChange={e => setNewSample({...newSample, trial_ref: e.target.value})} placeholder="e.g. T-03" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={addSample}>Add Sample</button>
              <button className="btn btn-secondary" onClick={() => setShowAddSample(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAddPanelist && (
        <div className="modal-overlay" onClick={() => setShowAddPanelist(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><div className="modal-title">Add Panelist</div><button className="modal-close" onClick={() => setShowAddPanelist(false)}>✕</button></div>
            {[['name','Name *','text'],['role','Role / Title','text'],['expertise','Expertise','text']].map(([k,lbl,t]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 4 }}>{lbl}</label>
                <input type={t} value={newPanelist[k]} onChange={e => setNewPanelist({...newPanelist, [k]: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
              </div>
            ))}
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={addPanelist}>Add</button>
              <button className="btn btn-secondary" onClick={() => setShowAddPanelist(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} mode="sensory" prefill={reportPrefill} api={API} filename={(session?.session_code || 'session') + '_sensory.pdf'} />
    </div>
  );
}
